// "Ingest a document": paste a proposed instrument (or attach a plain-text
// file) and have the worker ingest it into a draft definition for review. The
// result NEVER publishes itself — it lands in the editor exactly like a
// hand-built draft, and the registrar remains the sign-off.

import { useState, type JSX } from 'react';
import type { InstrumentDefinition } from '@cca/parishes-shared';
import { healthApi, HealthApiError } from '../api';

const MAX_CHARS = 120_000;

export function IngestOverlay({
  onClose,
  onIngested,
}: {
  onClose: () => void;
  onIngested: (definition: InstrumentDefinition) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setReading(true);
    setError(null);
    healthApi
      .ingestInstrument(trimmed)
      .then((res) => onIngested(res.definition))
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setError(
          code === 'rate_limited'
            ? 'Too many read-ins in the last hour — please wait a little and try again.'
            : code === 'text_too_short'
              ? 'That text is too short to be an instrument — paste the whole document.'
              : code === 'ingest_failed'
                ? 'The document could not be ingested into an instrument. Reword or trim it and try again, or build it by hand.'
                : 'Something went wrong while ingesting the document. Please try again.',
        );
      })
      .finally(() => setReading(false));
  };

  const attachFile = (file: File | undefined): void => {
    if (!file) return;
    file
      .text()
      .then((content) => setText(content.slice(0, MAX_CHARS)))
      .catch(() => setError('That file could not be read — paste the text instead.'));
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop click duplicates the explicit Close button inside the dialog
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(38, 34, 27, 0.45)',
        zIndex: 50,
        overflowY: 'auto',
        padding: '40px 16px',
      }}
      onClick={() => {
        if (!reading) onClose();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click handler only stops the backdrop-dismiss propagating; not an interaction */}
      <div
        className="card"
        style={{ maxWidth: 760, margin: '0 auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="health-section-heading">
          <span>Ingest a document</span>
          <button type="button" className="instr-ctl" disabled={reading} onClick={onClose}>
            Close
          </button>
        </h2>
        <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 10 }}>
          Paste a proposed instrument — from a Word or Google document, an email, anywhere — and it
          will be ingested into an editable draft: sections, scale questions, written questions, and
          any safeguarding or governance markings it can recognise. Nothing is published; you review
          the draft and publish it yourself. For Word or PDF files, open the document and copy the
          text; plain-text files can be attached directly.
        </p>

        <textarea
          className="health-textarea"
          style={{ minHeight: 260, fontSize: 12.5 }}
          value={text}
          maxLength={MAX_CHARS}
          placeholder="Paste the whole proposed instrument here..."
          aria-label="Proposed instrument text"
          disabled={reading}
          onChange={(e) => setText(e.target.value)}
        />

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 10,
          }}
        >
          <label
            className="btn btn-ghost btn-sm"
            style={{ cursor: reading ? 'default' : 'pointer' }}
          >
            Attach a text file
            <input
              type="file"
              accept=".txt,.md,text/plain,text/markdown"
              style={{ display: 'none' }}
              disabled={reading}
              onChange={(e) => attachFile(e.target.files?.[0])}
            />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={reading || text.trim().length < 200}
            onClick={submit}
          >
            {reading ? 'Ingesting the document...' : 'Ingest into a draft'}
          </button>
          {text.trim().length > 0 && text.trim().length < 200 && (
            <span style={{ fontSize: 12.5, color: 'var(--health-grey)' }}>
              Paste the whole document — a full instrument is longer than this.
            </span>
          )}
        </div>

        {reading && (
          <p style={{ fontSize: 13, color: 'var(--health-grey)', marginTop: 10 }}>
            Ingesting and structuring the document. This usually takes a minute or two — please keep
            this window open.
          </p>
        )}
        {error && (
          <div className="health-banner health-banner-red" role="alert" style={{ marginTop: 10 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
